import {
  Alert,
  Button,
  Card,
  Checkbox,
  Flex,
  Group,
  Modal,
  Text,
  Textarea,
  Title,
} from '@mantine/core';
import { TimeInput } from '@mantine/dates';
import { useForm } from '@mantine/form';
import { IconAlertCircle, IconClock } from '@tabler/icons';
import { useEffect, useState } from 'react';
import { PageModalState } from '..';
import { CustomCalendar } from '../../../components/custom-calendar';
import { getMyBankHour } from '../../../data/bank-hours';
import { useCheckBankHoursToUse } from '../../../data/hooks/bank-hours';
import {
  useCreateBankHourRequest,
  useCreateBankHourRequestPartial,
} from '../../../data/hooks/bank-hours-request';
import { BalanceText } from '../../../components/balance-text';
import { useIsMobile } from '../../../hooks/useIsMobile';
import {
  errorNotification,
  successNotification,
} from '../../../providers/mantine-notifications';
import { ListBankHourRequests } from './list-bank-hour-requests';

type RequestBankHourFormType = {
  description: string;
};

type CheckBankHourFormType = {
  isPartial?: boolean;
  hourBegin?: string;
  hourEnd?: string;
};

interface BankHourRequestProps {
  userId?: number;
  pageModal: PageModalState;
  setPageModal: (value: PageModalState | null) => void;
}

export function BankHourRequest({
  pageModal,
  setPageModal,
  userId,
}: BankHourRequestProps) {
  const isMobile = useIsMobile();
  const [selectedDates, setSelectedDates] = useState<Date[]>([]);

  const [refreshKey, setRefreshKey] = useState(0);

  const requestBankHourForm = useForm<RequestBankHourFormType>({
    initialValues: {
      description: '',
    },
  });

  const checkBankHourForm = useForm<CheckBankHourFormType>({
    initialValues: {
      isPartial: false,
      hourBegin: '',
      hourEnd: '',
    },
  });

  const {
    fetch: checkBankHoursToUseFetcher,
    response: checkBankHoursToUseResponse,
  } = useCheckBankHoursToUse();

  const [, setMyBalance] = useState<string>();

  const {
    fetch: createBankHourRequestFetcher,
    loading: createBankHourRequestLoader,
  } = useCreateBankHourRequest();

  const {
    fetch: createBankHourRequestPartialFetcher,
    loading: createBankHourRequestPartialLoader,
  } = useCreateBankHourRequestPartial();

  /** own balance, shown on the card and refreshed after each request */
  async function refreshBalance() {
    try {
      setMyBalance((await getMyBankHour()).composedAmount);
    } catch {
      setMyBalance(undefined); // the card just shows "--"
    }
  }

  function handleRequestCreated() {
    setSelectedDates([]);
    requestBankHourForm.reset();
    checkBankHourForm.reset();
    setPageModal(null);
    setRefreshKey((key) => key + 1);
    refreshBalance();
    successNotification({
      message: 'Solicitação criada com successo!',
      title: 'Aguarde a aprovação dos líderes',
    });
  }

  async function handleRequestBankHours(
    values: typeof requestBankHourForm.values,
  ) {
    if (!selectedDates.length) {
      errorNotification({
        message: 'tente novamente',
        title: 'Por favor selecione os dias',
      });
      return;
    }

    if (!checkBankHourForm.values.isPartial) {
      await createBankHourRequestFetcher({
        data: {
          date: selectedDates.map((item) => item?.toISOString()).join(','),
          description: values.description,
        },
        onSuccess: handleRequestCreated,
      });
    } else {
      if (
        !checkBankHourForm.values.hourBegin ||
        !checkBankHourForm.values.hourEnd
      ) {
        errorNotification({
          message: 'Por favor informe o período',
          title: 'tente novamente',
        });
        return;
      }
      await createBankHourRequestPartialFetcher({
        data: {
          date: selectedDates[0].toISOString(),
          begin: checkBankHourForm.values.hourBegin,
          end: checkBankHourForm.values.hourEnd,
          description: values.description,
        },
        onSuccess: handleRequestCreated,
      });
    }
  }

  async function handleCheckHours() {
    await checkBankHoursToUseFetcher({
      params: {
        users: Number(userId).toString(),
        interval: selectedDates.map((item) => item?.toISOString()).join(','),
        hourBegin: checkBankHourForm.values.hourBegin,
        hourEnd: checkBankHourForm.values.hourEnd,
        isPartial: checkBankHourForm.values.isPartial,
      },
    });
  }

  function handleSelectDates(dates: Date[]) {
    if (checkBankHourForm.values.isPartial) {
      if (dates.length > 1) {
        errorNotification({
          title: 'Solicitação parcial apenas para 1 dia',
          message: 'Por favor, selecione apenas um dia.',
        });
        return;
      }
      if (selectedDates.length && dates.length === 0) {
        setSelectedDates([]);
        return;
      }
    }
    setSelectedDates(dates);
  }

  useEffect(() => {
    refreshBalance();
  }, [userId]);

  useEffect(() => {
    if (selectedDates.length) {
      handleCheckHours();
    }
  }, [selectedDates.length, checkBankHourForm.values]);

  return (
    <>
      <Flex direction="column" gap={16}>
        <Card>
          <Title order={3}>Banco de horas ⏰</Title>
          <Text>
            Solicite abaixo seu pedido de banco de horas, ele será analisado
            pelo seu líder.
          </Text>
          {/* 
          <Flex direction="column" mt="sm">
            <Text size="sm" color="dimmed">
              Seu saldo atual
            </Text>
            {myBalance ? (
              <BalanceText value={myBalance} size="lg" />
            ) : (
              <Text fw="bold">--</Text>
            )}
          </Flex>
          */}
          <Group position="right" mt={'sm'}>
            <Button onClick={() => setPageModal('request-bank-hours')}>
              Solicitar
            </Button>
          </Group>

          <Title order={4} mt={16} mb={'xs'}>
            Solicitações
          </Title>

          <ListBankHourRequests userId={userId} refreshKey={refreshKey} />
        </Card>
      </Flex>
      <Modal
        fullScreen={isMobile}
        title="Solicitar Banco de Horas"
        opened={pageModal === 'request-bank-hours'}
        onClose={() => setPageModal(null)}
        closeOnClickOutside={false}
        withCloseButton={true}
        closeOnEscape={true}
        size={'xl'}
      >
        <form onSubmit={requestBankHourForm.onSubmit(handleRequestBankHours)}>
          <Flex gap={'md'} direction={isMobile ? 'column' : 'row'}>
            <Flex direction={'column'} gap={'xl'} w={isMobile ? '100%' : '70%'}>
              <Text>Selecione os dias que deseja solicitar:</Text>
              <CustomCalendar
                value={selectedDates}
                onChange={(dates) => handleSelectDates(dates)}
              />
            </Flex>

            <Flex direction={'column'} gap={'xl'} w={'100%'}>
              <Flex gap={'xl'}>
                <Flex direction={'column'} mr={16}>
                  <Text size={'sm'} c="dimmed">
                    Horas solicitadas
                  </Text>
                  <Text fw={'bold'}>
                    {checkBankHoursToUseResponse?.[0]?.totalResquestedTime ??
                      '00hrs 00min'}
                  </Text>
                </Flex>
                <Flex direction={'column'} mr={16}>
                  <Text size={'sm'} c="dimmed">
                    Horas disponíveis
                  </Text>
                  {checkBankHoursToUseResponse?.[0] ? (
                    <BalanceText
                      value={checkBankHoursToUseResponse[0].totalAvailableTime}
                    />
                  ) : (
                    <Text fw={'bold'}>--</Text>
                  )}
                </Flex>
              </Flex>

              {selectedDates.length > 0 &&
                checkBankHoursToUseResponse?.[0]?.isAllowedToUse === false && (
                  <Alert
                    icon={<IconAlertCircle size={16} />}
                    color="orange"
                    title="Saldo menor que o solicitado"
                  >
                    Você pode enviar o pedido, mas o RH só consegue aprovar se o
                    saldo cobrir as horas solicitadas.
                  </Alert>
                )}

              <Checkbox
                label="Parcial"
                description="Será uma solicitação parcial?"
                checked={checkBankHourForm.values.isPartial}
                onChange={(event) => {
                  const isChecked = event.currentTarget.checked;
                  checkBankHourForm.setFieldValue('isPartial', isChecked);
                  if (isChecked) {
                    setSelectedDates([]);
                  }
                }}
              />

              {checkBankHourForm.values.isPartial ? (
                <Flex gap={'lg'}>
                  <TimeInput
                    label="Inicio"
                    icon={<IconClock size={16} />}
                    clearable
                    {...checkBankHourForm.getInputProps('hourBegin')}
                  />
                  <TimeInput
                    label="Fim"
                    icon={<IconClock size={16} />}
                    clearable
                    {...checkBankHourForm.getInputProps('hourEnd')}
                  />
                </Flex>
              ) : (
                ''
              )}

              <Textarea
                required
                withAsterisk
                label="Observação"
                description="Descreva o motivo da solicitação"
                placeholder="comece a digitar.."
                maxLength={244}
                {...requestBankHourForm.getInputProps('description')}
              />
            </Flex>
          </Flex>

          <Group position="right">
            <Button
              type="submit"
              loading={
                createBankHourRequestLoader ||
                createBankHourRequestPartialLoader
              }
            >
              Solicitar
            </Button>
          </Group>
        </form>
      </Modal>
    </>
  );
}
